import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import { GoogleIdentity, GoogleRegister, Register } from '@interfaces/User';
import { AuthService } from '@service-features/auth.service';
import { BoButtonComponent } from '@shared-components/bo-button/bo-button.component';
import { LocationSelectorComponent } from '@shared-components/location-selector/location-selector.component';
import { NotificationService } from '@shared-services/handlers/notification.service';
import { passwordMatchValidator } from '@validators/password-match.validator';
import { ANGULAR_IMPORTS } from 'app/shared/imports/angular.imports';
import { MATERIAL_IMPORTS } from 'app/shared/imports/material.import';
import { ControlErrorMessagePipe } from 'app/shared/pipes/control-error-message.pipe';
import { Subscription, debounceTime } from 'rxjs';
import { InlineSpinnerComponent } from '@shared-components/inline-spinner/inline-spinner.component';

interface RegisterDraft {
  stepIndex?: number;
  companyForm?: {
    companyNameControl?: string;
    companyEmailControl?: string;
    companyProvinceControl?: string;
    companyCityControl?: string;
    companyAddressControl?: string;
    companyPhoneControl?: string;
  };
  userForm?: {
    userNameControl?: string;
    userEmailControl?: string;
  };
}

@Component({
  selector: 'gd-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
  standalone: true,
  imports: [
    InlineSpinnerComponent,
    ...ANGULAR_IMPORTS,
    ...MATERIAL_IMPORTS,
    CommonModule,
    BoButtonComponent,
    LocationSelectorComponent,
    ControlErrorMessagePipe,
  ],
})
export class RegisterComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatStepper) stepper!: MatStepper;

  private readonly REGISTER_DRAFT_KEY = 'register_draft';
  private readonly passwordValidators = [
    Validators.required,
    Validators.minLength(6),
    Validators.pattern(
      /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/,
    ),
  ];

  public isLoading: boolean = false;
  public hidePassword: boolean = true;
  public hideConfirmPassword: boolean = true;
  public isGoogleMode: boolean = false;
  public googleIdentity: GoogleIdentity | null = null;
  public selectedFile: string | null = null;

  readonly isLinear: boolean = true;
  readonly passwordHint: string = `La contraseña debe:<br>
&nbsp;&nbsp;- Contener al menos 6 caracteres<br>
&nbsp;&nbsp;- Al menos una letra mayúscula y minúscula<br>
&nbsp;&nbsp;- Al menos un número y un carácter especial.`;

  private loadingSubscription: Subscription;
  private formSubscriptions: Subscription[] = [];
  private draftStepIndex: number | null = null;

  public companyForm = this.fb.group({
    companyNameControl: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    companyEmailControl: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email],
    }),
    companyProvinceControl: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    companyCityControl: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    companyAddressControl: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    companyPhoneControl: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    companyLogoControl: new FormControl<File | null>(null, {
      validators: [Validators.required],
    }),
  });

  public userForm = this.fb.group(
    {
      userNameControl: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      userEmailControl: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required, Validators.email],
      }),
      userPasswordControl: new FormControl<string>('', {
        nonNullable: true,
        validators: this.passwordValidators,
      }),
      userConfirmPasswordControl: new FormControl<string>('', {
        nonNullable: true,
        validators: this.passwordValidators,
      }),
    },
    { validators: passwordMatchValidator() },
  );

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private notificationService: NotificationService,
  ) {
    this.loadingSubscription = this.authService.loading$.subscribe(
      (loading) => (this.isLoading = loading),
    );
  }

  get isGoogleAuthEnabled(): boolean {
    return this.authService.isGoogleAuthEnabled;
  }

  ngOnInit(): void {
    this.restoreDraft();
    void this.recoverGoogleIdentity();
  }

  ngAfterViewInit(): void {
    if (this.draftStepIndex !== null) {
      this.stepper.selectedIndex = this.draftStepIndex;
    }

    this.formSubscriptions.push(
      this.companyForm.valueChanges
        .pipe(debounceTime(300))
        .subscribe(() => this.saveDraft()),
      this.userForm.valueChanges
        .pipe(debounceTime(300))
        .subscribe(() => this.saveDraft()),
      this.stepper.selectionChange.subscribe(() => this.saveDraft()),
    );
  }

  public continueWithGoogle(): void {
    void this.connectGoogleAccount();
  }

  public onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) return;

    this.companyLogoControl.setValue(file);
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') this.selectedFile = reader.result;
    };
    reader.readAsDataURL(file);
  }

  public onRegister(): void {
    if (!this.checkValidity()) return;

    const companyValues = this.companyForm.getRawValue();
    const userValues = this.userForm.getRawValue();
    const logo = companyValues.companyLogoControl;

    if (this.isGoogleMode) {
      if (!this.googleIdentity) {
        this.notificationService.showToastError(
          'Volvé a vincular tu cuenta de Google para continuar.',
        );
        return;
      }

      const googleRegister: GoogleRegister = {
        company: {
          name: companyValues.companyNameControl,
          province: companyValues.companyProvinceControl,
          city: companyValues.companyCityControl,
          address: companyValues.companyAddressControl,
          phone: companyValues.companyPhoneControl,
          email: companyValues.companyEmailControl,
          logo: null,
        },
        user: {
          name: userValues.userNameControl,
          email: userValues.userEmailControl,
        },
      };

      this.authService.onGoogleRegister(googleRegister, logo);
      return;
    }

    if (!(logo instanceof File)) {
      this.notificationService.showToastError(
        'Debes seleccionar un logo válido',
      );
      return;
    }

    const register: Register = {
      company: {
        name: companyValues.companyNameControl,
        province: companyValues.companyProvinceControl,
        city: companyValues.companyCityControl,
        address: companyValues.companyAddressControl,
        phone: companyValues.companyPhoneControl,
        email: companyValues.companyEmailControl,
        logo: null,
      },
      user: {
        name: userValues.userNameControl,
        email: userValues.userEmailControl,
        password: userValues.userConfirmPasswordControl,
      },
    };

    this.authService.onRegister(register, logo);
  }

  public resetFroms(): void {
    void this.resetRegistration();
  }

  private async recoverGoogleIdentity(): Promise<void> {
    const identity = await this.authService.getGoogleRegistrationIdentity();
    if (identity) this.applyGoogleIdentity(identity);
  }

  private async connectGoogleAccount(): Promise<void> {
    const identity = await this.authService.startGoogleRegistration();
    if (identity) this.applyGoogleIdentity(identity);
  }

  private applyGoogleIdentity(identity: GoogleIdentity): void {
    this.googleIdentity = identity;
    this.isGoogleMode = true;
    this.setGoogleValidators();

    this.companyEmailControl.setValue(identity.email, { emitEvent: false });
    this.userEmailControl.setValue(identity.email, { emitEvent: false });
    this.userNameControl.setValue(identity.displayName, { emitEvent: false });
    this.companyEmailControl.disable({ emitEvent: false });
    this.userEmailControl.disable({ emitEvent: false });

    if (!this.companyLogoControl.value) {
      this.selectedFile = identity.photoUrl;
    }

    this.companyForm.updateValueAndValidity({ emitEvent: false });
    this.userForm.updateValueAndValidity({ emitEvent: false });
    this.saveDraft();
  }

  private setGoogleValidators(): void {
    this.companyLogoControl.clearValidators();
    this.userPasswordControl.clearValidators();
    this.userConfirmPasswordControl.clearValidators();
    this.userForm.clearValidators();
    this.userPasswordControl.reset('', { emitEvent: false });
    this.userConfirmPasswordControl.reset('', { emitEvent: false });
    this.companyLogoControl.updateValueAndValidity({ emitEvent: false });
    this.userPasswordControl.updateValueAndValidity({ emitEvent: false });
    this.userConfirmPasswordControl.updateValueAndValidity({
      emitEvent: false,
    });
  }

  private setLocalValidators(): void {
    this.companyLogoControl.setValidators([Validators.required]);
    this.userPasswordControl.setValidators(this.passwordValidators);
    this.userConfirmPasswordControl.setValidators(this.passwordValidators);
    this.userForm.setValidators(passwordMatchValidator());
    this.companyEmailControl.enable({ emitEvent: false });
    this.userEmailControl.enable({ emitEvent: false });
    this.companyLogoControl.updateValueAndValidity({ emitEvent: false });
    this.userPasswordControl.updateValueAndValidity({ emitEvent: false });
    this.userConfirmPasswordControl.updateValueAndValidity({
      emitEvent: false,
    });
    this.userForm.updateValueAndValidity({ emitEvent: false });
  }

  private async resetRegistration(): Promise<void> {
    await this.authService.cancelGoogleRegistration();

    this.googleIdentity = null;
    this.isGoogleMode = false;
    this.setLocalValidators();
    this.companyForm.reset();
    this.userForm.reset();
    this.stepper.reset();
    this.selectedFile = null;
    sessionStorage.removeItem(this.REGISTER_DRAFT_KEY);
  }

  private restoreDraft(): void {
    const raw = sessionStorage.getItem(this.REGISTER_DRAFT_KEY);
    if (!raw) return;

    try {
      const draft = JSON.parse(raw) as RegisterDraft;

      if (draft.companyForm) {
        this.companyForm.patchValue(draft.companyForm, { emitEvent: false });
      }
      if (draft.userForm) {
        this.userForm.patchValue(draft.userForm, { emitEvent: false });
      }
      if (typeof draft.stepIndex === 'number') {
        this.draftStepIndex = draft.stepIndex;
      }
    } catch {
      sessionStorage.removeItem(this.REGISTER_DRAFT_KEY);
    }
  }

  private saveDraft(): void {
    const companyValues = this.companyForm.getRawValue();
    const userValues = this.userForm.getRawValue();
    const draft: RegisterDraft = {
      stepIndex: this.stepper?.selectedIndex ?? this.draftStepIndex ?? 0,
      companyForm: {
        companyNameControl: companyValues.companyNameControl,
        companyEmailControl: companyValues.companyEmailControl,
        companyProvinceControl: companyValues.companyProvinceControl,
        companyCityControl: companyValues.companyCityControl,
        companyAddressControl: companyValues.companyAddressControl,
        companyPhoneControl: companyValues.companyPhoneControl,
      },
      userForm: {
        userNameControl: userValues.userNameControl,
        userEmailControl: userValues.userEmailControl,
      },
    };

    sessionStorage.setItem(this.REGISTER_DRAFT_KEY, JSON.stringify(draft));
  }

  private checkValidity(): boolean {
    const forms = [
      { form: this.companyForm, index: 0 },
      { form: this.userForm, index: 1 },
    ];

    for (const { form, index } of forms) {
      if (form.invalid) {
        this.notificationService.showToastError(
          'Todos los campos son obligatorios',
        );
        form.markAllAsTouched();
        this.stepper.selectedIndex = index;
        return false;
      }
    }

    if (
      !this.isGoogleMode &&
      this.userPasswordControl.value !== this.userConfirmPasswordControl.value
    ) {
      this.notificationService.showToastError(
        'Las contraseñas deben ser iguales',
      );
      this.userForm.markAllAsTouched();
      this.stepper.selectedIndex = 1;
      return false;
    }

    return true;
  }

  get companyNameControl(): FormControl<string> {
    return this.companyForm.controls.companyNameControl;
  }
  get companyEmailControl(): FormControl<string> {
    return this.companyForm.controls.companyEmailControl;
  }
  get companyProvinceControl(): FormControl<string> {
    return this.companyForm.controls.companyProvinceControl;
  }
  get companyCityControl(): FormControl<string> {
    return this.companyForm.controls.companyCityControl;
  }
  get companyAddressControl(): FormControl<string> {
    return this.companyForm.controls.companyAddressControl;
  }
  get companyPhoneControl(): FormControl<string> {
    return this.companyForm.controls.companyPhoneControl;
  }
  get companyLogoControl(): FormControl<File | null> {
    return this.companyForm.controls.companyLogoControl;
  }
  get userNameControl(): FormControl<string> {
    return this.userForm.controls.userNameControl;
  }
  get userEmailControl(): FormControl<string> {
    return this.userForm.controls.userEmailControl;
  }
  get userPasswordControl(): FormControl<string> {
    return this.userForm.controls.userPasswordControl;
  }
  get userConfirmPasswordControl(): FormControl<string> {
    return this.userForm.controls.userConfirmPasswordControl;
  }

  ngOnDestroy(): void {
    this.loadingSubscription.unsubscribe();
    this.formSubscriptions.forEach((subscription) =>
      subscription.unsubscribe(),
    );
  }
}
